import { OptionType } from "@/components/select-input/select-input";
import { cn, isDefined, mapSelf, NIL, notNil } from "@/lib/utils";
import React from "react";
import {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  PathValue,
} from "react-hook-form";
import {
  ClassNamesConfig,
  GroupBase,
  MultiValue,
  SingleValue,
} from "react-select";

type MappingOptionType = OptionType & { additional?: any[] };
export const useOptionsMapping = <
  Option extends MappingOptionType = MappingOptionType,
>(
  options: Option[],
  // additionalMapping?: Record<string, Option>,
) => {
  return React.useMemo(() => {
    const m = Object.fromEntries(
      options.flatMap((op) => [
        [op.value, op],
        ...(op?.additional?.map((value) => [value, op]) ?? []),
      ]),
    );
    return (v: Option["value"]) => m[v] as Option;
  }, [options]);
};

export type ArrayItem<T> = T extends any[] ? T[number] : T;

export type SelectInputOptionsMappingProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
> = { empty?: any } & (IsMulti extends true
  ? {
      mapping?: (value: ArrayItem<PathValue<TFieldValues, TName>>) => Option;
    } & (
      | {
          isMulti?: true;
          isClearable?: false;
          isNullable?: false;
          assign?: (value: PathValue<TFieldValues, TName>) => Option[];
          extract?: (option: Option[]) => PathValue<TFieldValues, TName>;
        }
      | {
          isMulti?: true;
          isClearable?: true;
          isNullable?: false;
          assign?: (
            value: PathValue<TFieldValues, TName>,
          ) => Option[] | undefined;
          extract?: (
            option?: Option[] | undefined,
          ) => PathValue<TFieldValues, TName>;
        }
      | {
          isMulti?: true;
          isClearable?: false;
          isNullable?: true;
          assign?: (value: PathValue<TFieldValues, TName>) => Option[] | null;
          extract?: (option: Option[] | null) => PathValue<TFieldValues, TName>;
        }
      | {
          isMulti?: true;
          isClearable?: true;
          isNullable?: true;
          assign?: (
            value: PathValue<TFieldValues, TName>,
          ) => Option[] | undefined | null;
          extract?: (
            option?: Option[] | null | undefined,
          ) => PathValue<TFieldValues, TName>;
        }
    )
  : {
      mapping?: (value: PathValue<TFieldValues, TName>) => Option;
    } & (
      | {
          isMulti?: false;
          isClearable?: false;
          isNullable?: false;
          assign?: (value: PathValue<TFieldValues, TName>) => Option;
          extract?: (option: Option) => PathValue<TFieldValues, TName>;
        }
      | {
          isMulti?: false;
          isClearable?: true;
          isNullable?: false;
          assign?: (
            value: PathValue<TFieldValues, TName>,
          ) => Option | undefined;
          extract?: (
            option?: Option | undefined,
          ) => PathValue<TFieldValues, TName>;
        }
      | {
          isMulti?: false;
          isClearable?: false;
          isNullable?: true;
          assign?: (value: PathValue<TFieldValues, TName>) => Option | null;
          extract?: (option: Option | null) => PathValue<TFieldValues, TName>;
        }
      | {
          isMulti?: false;
          isClearable?: true;
          isNullable?: true;
          assign?: (
            value: PathValue<TFieldValues, TName>,
          ) => Option | undefined | null;
          extract?: (
            option?: Option | null | undefined,
          ) => PathValue<TFieldValues, TName>;
        }
    ));

export type FieldOptionsMappingProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
> = {
  field: Pick<ControllerRenderProps<TFieldValues, TName>, "value">;
  cache?: OptionsCache<Value, Option>;
} & SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>;

type OptionsCache<Value, Option extends OptionType<Value>> = {
  forward: Record<any, Option>;
  reverse: Record<Option["label"], Option>;
};

export type FieldOptionsCacheProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  TOptionsCache extends OptionsCache<Value, Option>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  cache: TOptionsCache;
} & SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>;

// const fieldItems = <
//   TFieldValues extends FieldValues,
//   TName extends FieldPath<TFieldValues>,
//   IsMulti extends boolean,
// >({
//   field,
//   isMulti,
// }: {
//   field: Pick<ControllerRenderProps<TFieldValues, TName>, "value">;
//   isMulti?: IsMulti;
// }): any[] => {
//   return isMulti
//     ? Array.isArray(field.value)
//       ? field.value
//       : [field.value]
//     : [field.value];
// };

function useAssign<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
>({
  assign,
  // isClearable,
  // isMulti,
  // isNullable,
  isMulti,
  mapping,
  empty,
}: SelectInputOptionsMappingProps<
  TFieldValues,
  TName,
  Value,
  Option,
  IsMulti
>) {
  return React.useMemo(() => {
    if (assign) {
      return assign;
    }
    if (!mapping) return undefined;
    return (value) => {
      if (value === NIL) return empty;
      if (isMulti && Array.isArray(value)) {
        return value.map(mapping);
      }
      if (!isMulti && isDefined(value)) {
        return mapping(value);
      }
      return value;
    };
  }, [assign, mapping, isMulti, empty]) as typeof assign;
}

export const useFieldOptionsMapping = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
>(
  props: FieldOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>,
) => {
  const assign = useAssign(props);
  const { field, cache, isMulti, empty } = props;
  const value = React.useMemo(() => {
    const options = !assign ? field.value : assign(field.value);
    if (isMulti && Array.isArray(options) && isDefined(options)) {
      return (options as Option[]).map(
        (op) => cache?.forward?.[op.value] ?? cache?.reverse?.[op.label] ?? op,
      );
    }
    if (!isMulti && isDefined(options) && !Array.isArray(options)) {
      return mapSelf(
        options as Option,
        (op) => cache?.forward?.[op.value] ?? cache?.reverse?.[op.label] ?? op,
      );
    }
    return options;
  }, [assign, field.value, cache]);
  return notNil(value as Option[] | Option | null | undefined, empty);
};

function useExtract<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
>({
  extract,
  // isClearable,
  // isMulti,
  // isNullable,
  assign,
  mapping,
  empty,
}: SelectInputOptionsMappingProps<
  TFieldValues,
  TName,
  Value,
  Option,
  IsMulti
>) {
  return React.useMemo(() => {
    if (extract) {
      return extract;
    }
    return (option) => {
      if (!assign && !mapping) return option;
      if (!isDefined(option)) return empty;
      if (Array.isArray(option)) {
        const ret = option.map((op) => op.value);
        if (ret.length <= 0) {
          return empty;
        }
        return ret;
      }
      return option.value;
    };
  }, [extract, assign, mapping, empty]) as Exclude<typeof extract, undefined>;
}

export const useFieldOptionsMissing = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  TOptionsCache extends OptionsCache<Value, Option>,
>(
  props: FieldOptionsCacheProps<
    TFieldValues,
    TName,
    Value,
    Option,
    IsMulti,
    TOptionsCache
  >,
) => {
  const { field, cache, isMulti } = props;
  const assign = useAssign(props);
  const missing = React.useMemo(() => {
    const options = !assign ? field.value : assign(field.value);
    if (isMulti && Array.isArray(options) && isDefined(options)) {
      return (options as Option[]).filter(
        (op) => cache?.forward?.[op?.value] ?? cache?.reverse?.[op?.label],
      );
    }
    if (!isMulti && isDefined(options) && !Array.isArray(options)) {
      return [options as Option].filter(
        (op) => cache?.forward?.[op?.value] ?? cache?.reverse?.[op?.label],
      );
    }
    return;
  }, [assign, field.value, cache]);
  return missing as PathValue<TFieldValues, TName>[];
};

export const useFieldOptionsPartial = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  TOptionsCache extends OptionsCache<Value, Option>,
>(
  props: FieldOptionsCacheProps<
    TFieldValues,
    TName,
    Value,
    Option,
    IsMulti,
    TOptionsCache
  >,
) => {
  const assign = useAssign(props);
  const { field, cache, isMulti } = props;
  const missing = React.useMemo(() => {
    const options = !assign ? field.value : assign(field.value);
    if (isMulti && Array.isArray(options) && isDefined(options)) {
      return (options as Option[])
        .filter(
          (op) =>
            (op.value === undefined || op.value === op.label) &&
            !(op.value !== undefined && cache.forward[op.value]) &&
            !cache.reverse[op.label],
        )
        .map((op) => op.label);
    }
    if (!isMulti && isDefined(options) && !Array.isArray(options)) {
      return [options as Option]
        .filter(
          (op) =>
            (op.value === undefined || op.value === op.label) &&
            !(op.value !== undefined && cache.forward[op.value]) &&
            !cache.reverse[op.label],
        )
        .map((op) => op.label);
    }
    return [];
  }, [assign, field.value, cache]);
  return missing as PathValue<TFieldValues, TName>[];
};

export type FieldOptionsExtractProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
> = {
  field: Pick<ControllerRenderProps<TFieldValues, TName>, "value">;
  updateCache?: (options: Option[]) => void;
} & SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>;

export const useFieldOptionsExtract = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
>(
  props: FieldOptionsExtractProps<TFieldValues, TName, Value, Option, IsMulti>,
) => {
  const extract = useExtract(props);
  const {
    field,
    isClearable,
    isMulti,
    isNullable,
    assign,
    mapping,
    updateCache,
    empty,
  } = props;
  return React.useCallback(
    (option: MultiValue<Option> | SingleValue<Option>) => {
      if (updateCache) {
        const options = isMulti
          ? Array.isArray(option)
            ? option
            : isDefined(option)
              ? [option]
              : []
          : isDefined(option)
            ? [option]
            : [];
        updateCache(options);
      }
      const value = (
        !assign && !mapping ? option : extract(option as any)
      ) as PathValue<TFieldValues, TName>;
      return value;
    },
    [assign, mapping, extract, field.value],
  );
};

export const useFormSelectInputCommonClassNames = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isActive,
  inline,
  isMulti,
  classNames,
}: {
  isActive?: boolean;
  inline?: boolean;
  isMulti?: IsMulti;
  classNames?: ClassNamesConfig<Option, IsMulti, Group>;
}) => {
  return {
    ...classNames,
    control: (_s) =>
      cn(
        "!min-h-10 max-h-30 w-full py-1",
        inline && "transition-colors border-none group-hover/select:bg-ghost",
        isActive && !inline && "border-azure ring-azure ring-inset",
        _s.isFocused && "ring-azure ring-inset",
        classNames?.control?.(_s as any),
      ),
    valueContainer: (_s) =>
      cn(
        !isMulti &&
          isActive &&
          inline &&
          "text-azure group-hover/select:text-azure",
        !inline && "min-w-[100px]",
        classNames?.valueContainer?.(_s as any),
      ),
    placeholder: (_s) =>
      cn(
        inline && "group-hover/select:text-foreground",
        isActive && inline && "text-azure group-hover/select:text-azure",
        classNames?.placeholder?.(_s as any),
      ),
    menu: (_s) => cn("min-w-[200px]", classNames?.menu?.(_s)),
  };
};
